import { NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Target, Building2, Users, MapPin, FileText, FolderKanban, CheckSquare, BarChart3, Settings, Lock, Package2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { initials } from "@/lib/format";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/leads", label: "Leads", icon: Target },
  { to: "/companies", label: "Companies", icon: Building2 },
  { to: "/contacts", label: "Contacts", icon: Users, hideForAccounts: true },
  { to: "/site-visits", label: "Site Visits", icon: MapPin, soon: true, hideForAccounts: true },
  { to: "/quotations", label: "Quotations", icon: FileText, hideForAccounts: false },
  { to: "/projects", label: "Projects", icon: FolderKanban },
  { to: "/stock", label: "Stock", icon: Package2 },
  { to: "/tasks", label: "Tasks", icon: CheckSquare },
  { to: "/reports", label: "Reports", icon: BarChart3, soon: true },
  { to: "/settings", label: "Settings", icon: Settings },
];

export function Sidebar() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canSettings = ["ADMIN", "SALES_MANAGER"].includes(user?.role);
  const isAccounts = user?.role === "ACCOUNTS";
  const nav = NAV.filter((i) => {
    if (i.to === "/settings" && !canSettings) return false;
    if (i.hideForAccounts && isAccounts) return false;
    return true;
  });

  return (
    <aside className="hidden md:flex md:w-64 md:flex-col md:fixed md:inset-y-0 z-30" style={{ backgroundColor: "#0C1929" }}>
      <div className="flex items-center px-5 h-16 border-b border-white/5">
        <img src="/dazlac-logo.png" alt="Dazlac" className="h-8 w-auto" data-testid="sidebar-logo" />
      </div>

      <nav className="sidebar-scroll flex-1 overflow-y-auto px-3 py-4 space-y-0.5">
        {nav.map((item) => {
          const Icon = item.icon;
          if (item.soon) {
            return (
              <div key={item.to} data-testid={`nav-disabled-${item.label.toLowerCase().replace(/ /g, "-")}`}
                className="group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-slate-500 cursor-not-allowed select-none">
                <Icon className="h-[18px] w-[18px] opacity-60" />
                <span className="flex-1">{item.label}</span>
                <span className="flex items-center gap-1 rounded-full bg-white/5 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-slate-500">
                  <Lock className="h-2.5 w-2.5" /> Soon
                </span>
              </div>
            );
          }
          return (
            <NavLink key={item.to} to={item.to} data-testid={`nav-${item.label.toLowerCase().replace(/ /g, "-")}`}
              className={({ isActive }) =>
                `group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-150 ${
                  isActive ? "text-white shadow-sm" : "text-slate-300 hover:text-white hover:bg-white/5"
                }`
              }
              style={({ isActive }) => (isActive ? { backgroundColor: "#0057A8" } : {})}
            >
              <Icon className="h-[18px] w-[18px]" />
              <span>{item.label}</span>
            </NavLink>
          );
        })}
      </nav>

      <div className="border-t border-white/5 p-3">
        <button onClick={() => canSettings && navigate("/settings")} data-testid="sidebar-profile"
          className={`flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left transition ${canSettings ? "hover:bg-white/5 cursor-pointer" : "cursor-default"}`}>
          <div className="flex h-9 w-9 items-center justify-center rounded-full text-xs font-bold text-white" style={{ backgroundColor: "#E31E24" }}>
            {initials(user?.name)}
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold text-white">{user?.name}</div>
            <div className="truncate text-[11px] text-slate-400">{user?.email}</div>
          </div>
        </button>
      </div>
    </aside>
  );
}
